import { z } from "zod";

// Sync status enum
export const syncStatusSchema = z.enum(["in_sync", "out_of_sync", "missing", "unknown"]);
export type SyncStatus = z.infer<typeof syncStatusSchema>;

// Merge source: links a canonical group item to the source object in a specific app.
// Supplied at import time so sync updates the existing Qlik object (renaming it to the
// canonical name) instead of creating a duplicate — used when merging items that mean the
// same thing across apps but have different names.
export const mergeSourceSchema = z.object({
  groupAppId: z.string().uuid(),
  qlikObjectId: z.string().min(1),
});

export type MergeSource = z.infer<typeof mergeSourceSchema>;

// Group schema
export const groupSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string(),
  name: z.string().min(1),
  description: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type Group = z.infer<typeof groupSchema>;

// Group with counts (for list view)
export const groupWithCountsSchema = groupSchema.extend({
  appCount: z.number(),
  variableCount: z.number(),
  dimensionCount: z.number(),
  measureCount: z.number(),
});

export type GroupWithCounts = z.infer<typeof groupWithCountsSchema>;

// Create group input
export const createGroupInputSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
});

export type CreateGroupInput = z.infer<typeof createGroupInputSchema>;

// Update group input
export const updateGroupInputSchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
});

export type UpdateGroupInput = z.infer<typeof updateGroupInputSchema>;

// Group app schema
export const groupAppSchema = z.object({
  id: z.string().uuid(),
  groupId: z.string().uuid(),
  connectionId: z.string().uuid(),
  appId: z.string(),
  appName: z.string(),
  spaceName: z.string().nullable().optional(),
  ownerName: z.string().nullable().optional(),
  createdAt: z.string(),
  // Connection info for display
  connectionName: z.string().optional(),
  connectionBaseUrl: z.string().optional(),
  // Metadata cache info
  metadataFetchedAt: z.string().nullable().optional(),
});

export type GroupApp = z.infer<typeof groupAppSchema>;

// Add app to group input
export const addGroupAppInputSchema = z.object({
  connectionId: z.string().uuid(),
  appId: z.string().min(1),
  appName: z.string().min(1),
  spaceName: z.string().nullable().optional(),
  ownerName: z.string().nullable().optional(),
});

export type AddGroupAppInput = z.infer<typeof addGroupAppInputSchema>;

// Bulk add apps to group
export const bulkAddGroupAppsInputSchema = z.object({
  apps: z.array(addGroupAppInputSchema).min(1).max(500),
});

export type BulkAddGroupAppsInput = z.infer<typeof bulkAddGroupAppsInputSchema>;

export const bulkAddGroupAppsResultSchema = z.object({
  added: z.number().int(),
  skipped: z.number().int(),
});

export type BulkAddGroupAppsResult = z.infer<typeof bulkAddGroupAppsResultSchema>;

// Group variable schema
export const groupVariableSchema = z.object({
  id: z.string().uuid(),
  groupId: z.string().uuid(),
  name: z.string().min(1),
  definition: z.string().min(1),
  description: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type GroupVariable = z.infer<typeof groupVariableSchema>;

// Group variable with sync status matrix
export const groupVariableWithSyncSchema = groupVariableSchema.extend({
  syncStatus: z.array(
    z.object({
      groupAppId: z.string().uuid(),
      appId: z.string(),
      appName: z.string(),
      status: syncStatusSchema,
      remoteDefinition: z.string().nullable(),
      qlikVariableId: z.string().nullable(),
      lastCheckedAt: z.string().nullable(),
    }),
  ),
});

export type GroupVariableWithSync = z.infer<typeof groupVariableWithSyncSchema>;

// Create group variable input
export const createGroupVariableInputSchema = z.object({
  name: z.string().min(1, "Name is required"),
  definition: z.string().min(1, "Definition is required"),
  description: z.string().optional(),
  sources: z.array(mergeSourceSchema).optional(),
});

export type CreateGroupVariableInput = z.infer<typeof createGroupVariableInputSchema>;

// Update group variable input
export const updateGroupVariableInputSchema = z.object({
  name: z.string().min(1).optional(),
  definition: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
});

export type UpdateGroupVariableInput = z.infer<typeof updateGroupVariableInputSchema>;

// Sync status record schema
export const groupVariableSyncStatusSchema = z.object({
  id: z.string().uuid(),
  variableId: z.string().uuid(),
  groupAppId: z.string().uuid(),
  status: syncStatusSchema,
  remoteDefinition: z.string().nullable(),
  qlikVariableId: z.string().nullable(),
  lastCheckedAt: z.string().nullable(),
});

export type GroupVariableSyncStatus = z.infer<typeof groupVariableSyncStatusSchema>;

// Update sync status input (from worker)
export const updateSyncStatusInputSchema = z.object({
  variableId: z.string().uuid(),
  groupAppId: z.string().uuid(),
  status: syncStatusSchema,
  remoteDefinition: z.string().nullable().optional(),
  qlikVariableId: z.string().nullable().optional(),
  lastCheckedAt: z.string().optional(),
});

export type UpdateSyncStatusInput = z.infer<typeof updateSyncStatusInputSchema>;

// API Response schemas
export const listGroupsResponseSchema = z.object({
  data: z.array(groupWithCountsSchema),
  nextCursor: z.string().uuid().optional(),
});

export type ListGroupsResponse = z.infer<typeof listGroupsResponseSchema>;

export const groupResponseSchema = z.object({
  data: groupSchema,
});

export type GroupResponse = z.infer<typeof groupResponseSchema>;

export const listGroupAppsResponseSchema = z.object({
  data: z.array(groupAppSchema),
});

export type ListGroupAppsResponse = z.infer<typeof listGroupAppsResponseSchema>;

export const listGroupVariablesResponseSchema = z.object({
  data: z.array(groupVariableWithSyncSchema),
});

export type ListGroupVariablesResponse = z.infer<typeof listGroupVariablesResponseSchema>;

export const groupVariableResponseSchema = z.object({
  data: groupVariableWithSyncSchema,
});

export type GroupVariableResponse = z.infer<typeof groupVariableResponseSchema>;

// ----------------------------
// Group Dimensions (Sync Groups - Master Items)
// ----------------------------

// Group dimension schema
export const groupDimensionSchema = z.object({
  id: z.string().uuid(),
  groupId: z.string().uuid(),
  title: z.string().min(1),
  description: z.string().nullable(),
  fieldDefs: z.array(z.string()),
  fieldLabels: z.array(z.string()),
  grouping: z.enum(["N", "H", "C"]),
  tags: z.array(z.string()),
  labelExpression: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type GroupDimension = z.infer<typeof groupDimensionSchema>;

// Group dimension with sync status matrix
export const groupDimensionWithSyncSchema = groupDimensionSchema.extend({
  syncStatus: z.array(
    z.object({
      groupAppId: z.string().uuid(),
      appId: z.string(),
      appName: z.string(),
      status: syncStatusSchema,
      remoteFieldDefs: z.array(z.string()).nullable(),
      qlikDimensionId: z.string().nullable(),
      lastCheckedAt: z.string().nullable(),
    }),
  ),
});

export type GroupDimensionWithSync = z.infer<typeof groupDimensionWithSyncSchema>;

// Create group dimension input
export const createGroupDimensionInputSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  fieldDefs: z.array(z.string()).min(1, "At least one field is required"),
  fieldLabels: z.array(z.string()).optional().default([]),
  grouping: z.enum(["N", "H", "C"]).optional().default("N"),
  tags: z.array(z.string()).optional().default([]),
  labelExpression: z.string().optional().default(""),
  sources: z.array(mergeSourceSchema).optional(),
});

export type CreateGroupDimensionInput = z.infer<typeof createGroupDimensionInputSchema>;

// Update group dimension input
export const updateGroupDimensionInputSchema = z.object({
  title: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  fieldDefs: z.array(z.string()).min(1).optional(),
  fieldLabels: z.array(z.string()).optional(),
  grouping: z.enum(["N", "H", "C"]).optional(),
  tags: z.array(z.string()).optional(),
  labelExpression: z.string().optional(),
});

export type UpdateGroupDimensionInput = z.infer<typeof updateGroupDimensionInputSchema>;

// Dimension sync status record
export const groupDimensionSyncStatusSchema = z.object({
  id: z.string().uuid(),
  dimensionId: z.string().uuid(),
  groupAppId: z.string().uuid(),
  status: syncStatusSchema,
  remoteFieldDefs: z.array(z.string()).nullable(),
  qlikDimensionId: z.string().nullable(),
  lastCheckedAt: z.string().nullable(),
});

export type GroupDimensionSyncStatus = z.infer<typeof groupDimensionSyncStatusSchema>;

// Update dimension sync status input (from worker)
export const updateDimensionSyncStatusInputSchema = z.object({
  dimensionId: z.string().uuid(),
  groupAppId: z.string().uuid(),
  status: syncStatusSchema,
  remoteFieldDefs: z.array(z.string()).nullable().optional(),
  qlikDimensionId: z.string().nullable().optional(),
  lastCheckedAt: z.string().optional(),
});

export type UpdateDimensionSyncStatusInput = z.infer<typeof updateDimensionSyncStatusInputSchema>;

// API response schemas
export const listGroupDimensionsResponseSchema = z.object({
  data: z.array(groupDimensionWithSyncSchema),
});

export type ListGroupDimensionsResponse = z.infer<typeof listGroupDimensionsResponseSchema>;

export const groupDimensionResponseSchema = z.object({
  data: groupDimensionWithSyncSchema,
});

export type GroupDimensionResponse = z.infer<typeof groupDimensionResponseSchema>;

// ----------------------------
// Group Measures (Sync Groups - Master Items)
// ----------------------------

// Group measure schema
export const groupMeasureSchema = z.object({
  id: z.string().uuid(),
  groupId: z.string().uuid(),
  title: z.string().min(1),
  description: z.string().nullable(),
  expression: z.string().min(1),
  label: z.string().nullable(),
  tags: z.array(z.string()),
  numberFormat: z.string().nullable(),
  color: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type GroupMeasure = z.infer<typeof groupMeasureSchema>;

// Group measure with sync status matrix
export const groupMeasureWithSyncSchema = groupMeasureSchema.extend({
  syncStatus: z.array(
    z.object({
      groupAppId: z.string().uuid(),
      appId: z.string(),
      appName: z.string(),
      status: syncStatusSchema,
      remoteExpression: z.string().nullable(),
      qlikMeasureId: z.string().nullable(),
      lastCheckedAt: z.string().nullable(),
    }),
  ),
});

export type GroupMeasureWithSync = z.infer<typeof groupMeasureWithSyncSchema>;

// Create group measure input
export const createGroupMeasureInputSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  expression: z.string().min(1, "Expression is required"),
  label: z.string().optional(),
  tags: z.array(z.string()).optional().default([]),
  numberFormat: z.string().optional(),
  color: z.string().optional(),
  sources: z.array(mergeSourceSchema).optional(),
});

export type CreateGroupMeasureInput = z.infer<typeof createGroupMeasureInputSchema>;

// Update group measure input
export const updateGroupMeasureInputSchema = z.object({
  title: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  expression: z.string().min(1).optional(),
  label: z.string().nullable().optional(),
  tags: z.array(z.string()).optional(),
  numberFormat: z.string().nullable().optional(),
  color: z.string().nullable().optional(),
});

export type UpdateGroupMeasureInput = z.infer<typeof updateGroupMeasureInputSchema>;

// Measure sync status record
export const groupMeasureSyncStatusSchema = z.object({
  id: z.string().uuid(),
  measureId: z.string().uuid(),
  groupAppId: z.string().uuid(),
  status: syncStatusSchema,
  remoteExpression: z.string().nullable(),
  qlikMeasureId: z.string().nullable(),
  lastCheckedAt: z.string().nullable(),
});

export type GroupMeasureSyncStatus = z.infer<typeof groupMeasureSyncStatusSchema>;

// Update measure sync status input (from worker)
export const updateMeasureSyncStatusInputSchema = z.object({
  measureId: z.string().uuid(),
  groupAppId: z.string().uuid(),
  status: syncStatusSchema,
  remoteExpression: z.string().nullable().optional(),
  qlikMeasureId: z.string().nullable().optional(),
  lastCheckedAt: z.string().optional(),
});

export type UpdateMeasureSyncStatusInput = z.infer<typeof updateMeasureSyncStatusInputSchema>;

// API response schemas
export const listGroupMeasuresResponseSchema = z.object({
  data: z.array(groupMeasureWithSyncSchema),
});

export type ListGroupMeasuresResponse = z.infer<typeof listGroupMeasuresResponseSchema>;

export const groupMeasureResponseSchema = z.object({
  data: groupMeasureWithSyncSchema,
});

export type GroupMeasureResponse = z.infer<typeof groupMeasureResponseSchema>;

// ----------------------------
// Group Detail (with all item types)
// ----------------------------

// Group detail with apps, variables, dimensions, and measures
export const groupDetailSchema = groupSchema.extend({
  apps: z.array(groupAppSchema),
  variables: z.array(groupVariableWithSyncSchema),
  dimensions: z.array(groupDimensionWithSyncSchema),
  measures: z.array(groupMeasureWithSyncSchema),
});

export type GroupDetail = z.infer<typeof groupDetailSchema>;

export const groupDetailResponseSchema = z.object({
  data: groupDetailSchema,
});

export type GroupDetailResponse = z.infer<typeof groupDetailResponseSchema>;
